/* @jsx h */
import { defineComponent, h, inject, ref } from "vue";
import {
  DateRange,
  DateRangePicker,
  useLocalization,
  BottomSheet,
} from "@lemmonhq/uikit";
import lemmonIcon from "@app/images/icons/favico.png";
import type { Ref, VNodeChild } from "vue";

type MobileOverviewHeaderProps = {
  dateRange: DateRange;
  onDateRangeChange: (dateRange: DateRange) => void;
  revenue?: string;
  restaurantName?: string;
  canFilter?: boolean;
  currency: string;
  additionalFilters?: () => VNodeChild;
};

export const MobileOverviewHeader = defineComponent<MobileOverviewHeaderProps>({
  name: "MobileOverviewHeader",
  props: [
    "dateRange",
    "onDateRangeChange",
    "revenue",
    "restaurantName",
    "canFilter",
    "currency",
    "additionalFilters",
  ],
  setup(props) {
    const { t } = useLocalization();
    const scrollProgress = inject<Ref<number>>("mobileRevenueScrollProgress");
    const filterOpen = ref(false);

    return () => {
      const p = scrollProgress?.value ?? 0;
      const revenueStyle = {
        opacity: p,
        transform: `translateY(${(1 - p) * 10}px)`,
        pointerEvents: "none" as const,
      };

      return (
        <>
          <header class="sticky top-0 z-20 flex items-center gap-3 border-b glossy-bg px-2 py-2.5 md:hidden">
            <img
              src={lemmonIcon}
              alt="Lemmon"
              class="size-9 shrink-0 rounded-xl border bg-base-200 p-1 object-contain shadow-sm hidden"
            />
            {/* Name: left-aligned, always visible */}
            <div class="min-w-0 flex-1 truncate text-base text-accent/70">
              {props.restaurantName}
            </div>

            {/* Mobile: with no extra filters, the date picker's own trigger
                opens its own single sheet directly — no need for a
                "Filters" launcher button wrapping it in a second sheet. */}
            {props.canFilter !== false ? (
              props.additionalFilters ? (
                <button
                  class="btn btn-sm btn-ghost relative gap-1.5 pl-2 pr-3"
                  onClick={() => {
                    filterOpen.value = true;
                  }}
                >
                  <span class="material-symbols-outlined text-base">
                    tune
                  </span>
                  <span class="text-sm font-medium">
                    {t("orders.filter.filters" as any)}
                  </span>
                </button>
              ) : (
                <DateRangePicker
                  className="[&>button]:btn-sm [&>button]:btn-ghost [&>button]:pl-2 [&>button]:pr-3"
                  value={props.dateRange}
                  onChange={props.onDateRangeChange}
                />
              )
            ) : null}

            {/* Revenue: absolutely centered, scroll-driven */}
            <div
              class="pointer-events-none absolute inset-0 flex items-center justify-center text-base font-semibold tracking-tight text-accent"
              style={revenueStyle}
            >
              {props.revenue ? `${props.currency} ${props.revenue}` : null}
            </div>
          </header>

          {/* Desktop: standard DateRangePicker rendered outside the mobile header */}

          {filterOpen.value && (
            <BottomSheet
              isOpen={true}
              title={
                props.additionalFilters
                  ? t("orders.filter.filters" as any)
                  : t("reports.general.date_range" as any)
              }
              onClose={() => {
                filterOpen.value = false;
              }}
            >
              <div class="flex flex-col gap-4 p-4">
                {props.additionalFilters?.()}
                <div class="flex flex-col gap-2">
                  {props.additionalFilters ? (
                    <div class="text-sm font-semibold">
                      {t("reports.general.date_range" as any)}
                    </div>
                  ) : null}
                  <DateRangePicker
                    value={props.dateRange}
                    onChange={(range) => {
                      props.onDateRangeChange(range);
                      filterOpen.value = false;
                    }}
                  />
                </div>
              </div>
            </BottomSheet>
          )}
        </>
      );
    };
  },
});
